/* -------------------------------------------------------------------------- */
/*                                  Packages                                  */
/* -------------------------------------------------------------------------- */
import { Licensing, MBHeadshot, OutlinedField } from "@abmg-components"
import ProgressBar from "@components/global/funnels/ProgressBar"
import { createTotalExpertContact, currencyFormatter } from "@utils/utils"
import { Link } from "gatsby"
import * as React from "react"
import { Controller, useForm } from "react-hook-form"
import RangeSlider from "react-range-slider-input"
import { twMerge } from "tw-merge"
import { titleCase } from "title-case"
import { getCities } from "@mardillu/us-cities-utils"
import { Check, Loader } from "react-feather"

/* -------------------------------------------------------------------------- */
/*                             External Components                            */
/* -------------------------------------------------------------------------- */
import Disclosures from "@components/global/disclosures"

/* -------------------------------------------------------------------------- */
/*                              Default Function                              */
/* -------------------------------------------------------------------------- */
export default function Form(props) {
  const [currentStep, setCurrentStep] = React.useState(0)
  const {
    control,
    handleSubmit,
    register,
    formState: { errors, defaultValues },
    watch,
    reset,
    setFocus,
  } = useForm({
    mode: "onTouched",
  })

  const watchAllFields = watch()
  const stepHeadingClass = `font-semibold leading-[44px] text-balance text-[36px] -tracking-[.72px]`
  const largeHeadingClass = `font-semibold leading-[72px] text-balance text-[56px] -tracking-[1px]`
  const steps = [
    {
      description: {
        text: (
          <>
            If you’re here, it’s because someone you know trusts us — and now
            we’d love to help you too. Please share their name, then click Start
            to begin a quick, three-step form.
          </>
        ),
      },
      fieldGroups: [
        {
          fields: [
            {
              name: "referrer_first_name",
              placeholder: "First Name",
              type: "name",
              errorMessage: `Please enter the first name of the person who referred you.`,
            },
            {
              name: "referrer_last_name",
              placeholder: "Last Name",
              type: "name",
              errorMessage: `Please enter the last name of the person who referred you.`,
            },
          ],
          heading: {
            text: `Who Told You About Us?`,
          },
        },
      ],
      heading: {
        classes: {
          default: largeHeadingClass,
        },
        text: <>Excited to Work with You!</>,
      },
      useTabToNextField: true,
    },
    {
      description: {
        text: (
          <>
            We’re always here to make things simple. Start by sharing your
            information so I can reach out and guide you through your next
            steps.
          </>
        ),
      },
      consent: {
        fields: [
          {
            className: `bg-grey-1 flex flex-col !mb-0 [--lineHeight:16px] [&_label]:!leading-[var(--lineHeight)] [&_label]:!text-[14px] [&_label]:!text-grey-7 [&_label]:before:!top-[calc(var(--lineHeight)/2)]`,
            errorMessage: `Please read the terms and conditions and check the box to agree.`,
            field: "checkbox",
            id: "terms",
            labelText: `I have read and agree to the terms and conditions`,
            name: "terms",
            rules: {
              required: true,
            },
          },
        ],
        text: <Disclosures disclosure="sms" />,
      },
      fieldGroups: [
        {
          fields: [
            {
              name: "first_name",
              placeholder: "First Name",
              type: "name",
              errorMessage: `Please enter your first name.`,
            },
            {
              name: "last_name",
              placeholder: "Last Name",
              type: "name",
              errorMessage: `Please enter your last name.`,
            },
            {
              name: "email",
              placeholder: "Email Address",
              type: "email",
            },
            {
              name: "phone_cell",
              placeholder: "Phone Number",
              type: "phone",
            },
          ],
          heading: {
            text: `Contact Information`,
          },
        },
      ],
      heading: {
        classes: {
          default: stepHeadingClass,
        },
        text: <>Your Contact Details</>,
      },
      useTabToNextField: true,
    },
    {
      description: {
        text: (
          <>
            We’re always here to make things simple. Start by sharing your
            information so I can reach out and guide you through your next
            steps.
          </>
        ),
      },
      fieldGroups: [
        {
          fields: [
            {
              field: "radio",
              labelText: "Purchase",
              name: "purchase_or_refinance",
              value: "purchase",
              id: "purchase",
              errorMessage: `Please select an option.`,
              rules: {
                required: true,
              },
            },
            {
              field: "radio",
              labelText: "Refinance",
              name: "purchase_or_refinance",
              value: "refinance",
              id: "refinance",
              hideErrorMessage: true,
            },
          ],
          heading: {
            text: `Are you interested in a Purchasing or Refinancing?`,
          },
        },
        {
          fields: [
            {
              defaultValue: [1000, 2000],
              name: "desired_monthly_payment",
              field: "range",
              label: "Desired Monthly Payment",
              max: 5000,
              min: 800,
              step: 100,
            },
          ],
          heading: {
            text: `What monthly payment range feels comfortable for you?`,
          },
        },
      ],
      heading: {
        classes: {
          default: stepHeadingClass,
        },
        text: <>Your Loan Needs</>,
      },
    },
    {
      description: {
        text: (
          <>
            Almost there! Knowing your location helps us know how to serve you —
            right where you are.
          </>
        ),
      },
      fieldGroups: [
        {
          fields: [
            {
              classes: {
                default: `[&:focus-within_[class*=placeholder]]:hidden`,
              },
              field: "select",
              name: "state_of_home_purchase",
              placeholder: "State",
              selectOptions: props.bankerData.licenses.map(license => {
                const state = license.slice(0, 2)
                return {
                  value: state,
                  text: state,
                }
              }),
              rules: {
                required: true,
              },
              errorMessage: `Please select a state.`,
            },
            {
              classes: {
                default: `[&:focus-within_[class*=placeholder]]:hidden`,
              },
              disabled: !watchAllFields["state_of_home_purchase"]
                ? true
                : false,
              field: "select",
              name: "city_of_home_purchase",
              selectOptions: watchAllFields["state_of_home_purchase"]
                ? getCities(watchAllFields["state_of_home_purchase"].value)
                    .sort((cityA, cityB) => {
                      if (cityA.name > cityB.name) return 1
                      if (cityA.name === cityB.name) return 0
                      return -1
                    })
                    .map(({ name }) => {
                      let city = titleCase(name.toLowerCase())
                      return {
                        text: city,
                        value: city,
                      }
                    })
                : [],
              placeholder: "City",
              rules: {
                required: true,
              },
              errorMessage: `Please select a city.`,
            },
          ],
          heading: {
            text: `Location Information`,
          },
        },
      ],
      heading: {
        classes: {
          default: stepHeadingClass,
        },
        text: <>Your Property’s Location</>,
      },
    },
    {
      description: {
        text: (
          <>
            Your information has been received. I will reach out soon to help
            you take the next step toward your home goals. And — most
            importantly — thank you for trusting Atlantic Bay!
          </>
        ),
      },
      heading: {
        classes: {
          default: largeHeadingClass,
        },
        text: <>Thank you!</>,
      },
      icon: (
        <div className="bg-pacific-blue flex h-[120px] items-center justify-center rounded-full self-center w-[120px]">
          <Check className="stroke-grey-1 h-[33px] w-[47px]" strokeWidth={5} />
        </div>
      ),
    },
  ]

  React.useEffect(() => {
    let defaultValues = {}
    steps.forEach(step => {
      step.fieldGroups?.forEach(fieldGroup => {
        fieldGroup.fields.forEach(({ defaultValue, name }) => {
          if (!defaultValues[name]) defaultValues[name] = defaultValue || ""
        })
      })
    })
    reset(defaultValues)
  }, [])

  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const formatFormData = ({ formData, mbEmail }) => {
    const requiredFieldEmptyValueString = "N/A"
    const defaultTEEmail = `lauralender@atlanticbay.com`
    let teEmail =
      formData["email"] === "test@test.com"
        ? defaultTEEmail
        : mbEmail || defaultTEEmail
    const data = {
      customFields: {
        desired_monthly_payment: Array.isArray(
          formData["desired_monthly_payment"],
        )
          ? `${currencyFormatter
              .format(formData["desired_monthly_payment"][0])
              .replace("$", "")}-${currencyFormatter
              .format(formData["desired_monthly_payment"][1])
              .replace("$", "")}`
          : undefined,
        original_lead_source: "MB-Web-Referral",
        location_of_home_purchase:
          !formData["city_of_home_purchase"] ||
          !formData["state_of_home_purchase"]
            ? undefined
            : `${formData["city_of_home_purchase"].value}, ${formData["state_of_home_purchase"].value}`,
        purchase_or_refinance: !formData["purchase_or_refinance"]
          ? undefined
          : formData["purchase_or_refinance"],
        referrer_name_: `${formData["referrer_first_name"]} ${formData["referrer_last_name"]}`,
      },
      email: formData["email"],
      first_name: formData["first_name"] || requiredFieldEmptyValueString,
      last_name: formData["last_name"] || requiredFieldEmptyValueString,
      owner: { email: teEmail },
      phone_cell: formData["phone_cell"],
      source: "Website Lead",
    }

    return data
  }

  const formContainerRef = React.useRef(null)

  const navigateStep = step => {
    handleSubmit(
      async formData => {
        formContainerRef.current.scrollIntoView()
        if (step < steps.length - 1) {
          setCurrentStep(step)
        } else {
          try {
            setIsSubmitted(true)
            const formattedData = formatFormData({
              formData,
              mbEmail: props.bankerData.crmEmail,
            })

            await createTotalExpertContact({
              contactData: formattedData,
              optInData: {
                phone_number: formData["phone_cell"],
                user: {
                  ...formattedData.owner,
                },
              },
            })
            setCurrentStep(steps.length - 1)
            setIsSubmitted(false)
          } catch (err) {
            console.error(err)
            setIsSubmitted(false)
          }
        }
      },
      errors => {
        if (isSubmitted) setIsSubmitted(false)
      },
    )()
  }

  return (
    <div className="bg-grey-1 [padding-block:50px_100px]">
      {/* Main Container */}
      <div className="grid gap-[46px] justify-items-center max-w-[1152px] mx-auto w-full min-desktop4:grid-cols-[1fr_auto] ">
        {/* Form Container */}
        <div
          className="bg-white flex flex-col gap-[16px] justify-center max-w-full min-h-[806px] [padding:32px_43px] relative rounded-[12px] w-[346px] min-tablet2:w-[646px] min-desktop4:w-auto"
          ref={formContainerRef}
        >
          <ProgressBar
            classes={{
              progressContainer: `bg-pacific-blue rounded-tl-[12px] ${
                currentStep === steps.length - 1 ? "rounded-tr-[12px]" : ""
              }`,
              componentContainer: `absolute bg-white left-0 top-0 max-w-full`,
            }}
            totalSteps={steps.length - 1}
            currentStep={currentStep}
          />
          {steps[currentStep].icon && steps[currentStep].icon}
          <h3
            className={twMerge(
              `font-variant-numeric--primary sans text-center text-grey-8 ${
                steps[currentStep].heading.classes?.default || ""
              }`,
            )}
          >
            {steps[currentStep].heading.text}
          </h3>
          <div
            className={twMerge(
              `font-variant-numeric--primary text-center text-grey-5 ${
                steps[currentStep].description.classes?.default || ""
              }`,
            )}
          >
            {steps[currentStep].description.text}
          </div>
          {currentStep > 0 && currentStep < steps.length - 1 && (
            <div className="font-variant-numeric--primary bg-pacific-blue font-bold leading-[20px] [margin-block:39px] [padding:3px_9px] rounded-[100px] self-center text-[16px] text-grey-1 uppercase">
              Step {currentStep} of {steps.length - 2}
            </div>
          )}
          {steps[currentStep].fieldGroups?.map(fieldGroup => {
            let groupedFieldNames = []

            if (steps[currentStep].useTabToNextField) {
              steps[currentStep].fieldGroups?.forEach(fieldGroup => {
                fieldGroup.fields.forEach(({ name }) => {
                  if (groupedFieldNames.indexOf(name) === -1)
                    groupedFieldNames.push(name)
                })
              })
            }

            return (
              <div
                key={`field-group-${fieldGroup.fields[0].name}`}
                className={twMerge(
                  `flex flex-wrap gap-[24px] [padding-block:30px] ${
                    fieldGroup.classes?.default ? "" : ""
                  }`,
                )}
              >
                {/* Field Group Heading */}
                <div
                  className={twMerge(
                    `font-variant-numeric--primary font-semibold leading-[28px] text-[20px] text-grey-8 w-full ${
                      fieldGroup.heading.classes?.default || ""
                    }`,
                  )}
                >
                  {fieldGroup.heading.text}
                </div>
                {fieldGroup.fields.map(formField => {
                  return formField.field !== "range" ? (
                    <OutlinedField
                      key={formField.id || formField.name}
                      className={twMerge(
                        `[&.outlined-field]:mb-0 [&_label]:text-grey-5 ${
                          formField.hideErrorMessage
                            ? `[&_.message-container]:hidden`
                            : ""
                        } ${formField.classes?.default || ""}`,
                      )}
                      control={control}
                      errors={errors}
                      watch={watch}
                      register={register}
                      Controller={Controller}
                      setFocus={setFocus}
                      groupedFieldNames={groupedFieldNames}
                      useTabToNextField={
                        steps[currentStep].useTabToNextField &&
                        (!formField.field || formField.field === "input")
                      }
                      {...formField}
                    />
                  ) : (
                    <div className=" w-full" key={`range-${formField.name}`}>
                      {/* Selected Values Container */}
                      <div className="flex justify-between mb-[16px] [&_*]:font-variant-numeric--primary [&_*]:leading-[44px] [&_*]:text-[20px] [&_*]:text-grey-5 [&_*]:-tracking-[.4px]">
                        <div>
                          {currencyFormatter.format(
                            watchAllFields[formField.name]
                              ? watchAllFields[formField.name][0]
                              : defaultValues[formField.name][0],
                          )}{" "}
                          -{" "}
                          {currencyFormatter.format(
                            watchAllFields[formField.name]
                              ? watchAllFields[formField.name][1]
                              : defaultValues[formField.name][1],
                          )}
                        </div>
                      </div>
                      <Controller
                        control={control}
                        name={formField.name}
                        rules={{
                          required: true,
                        }}
                        render={({ field }) => {
                          const fieldProps = {
                            ...field,
                            ...formField,
                          }
                          return (
                            <RangeSlider
                              {...fieldProps}
                              className={`!h-[6px] w-full ${"[&]:!bg-[rgba(0,_60,_72,_0.16)] [&_div]:!bg-[#278DE5]"}  [&_[class*=thumb]]:border-[6px] [&_[class*=thumb]]:border-white [&_[class*=thumb]]:shadow-sm [&_[class*=thumb]]:!h-[32px] [&_[class*=thumb]]:!w-[32px] `}
                              onInput={values => {
                                field.onChange(values)
                              }}
                            />
                          )
                        }}
                      />
                    </div>
                  )
                })}
              </div>
            )
          })}
          {/* Opt-in Consent */}
          {steps[currentStep].consent && (
            <div className="flex flex-col gap-[14px] [padding-block:30px]">
              {steps[currentStep].consent.fields.map(field => {
                return (
                  <OutlinedField
                    key={field.id || field.name}
                    className={twMerge(
                      `[&.outlined-field]:mb-0 [&_label]:!text-grey-5 ${
                        field.classes?.default || ""
                      }`,
                    )}
                    control={control}
                    errors={errors}
                    watch={watch}
                    register={register}
                    Controller={Controller}
                    {...field}
                  />
                )
              })}
              <div className="[&_*]:font-variant-numeric--primary [&_*]:leading-[21px] [&_*]:text-[10px] [&_*]:text-grey-6 [&_a]:text-pacific-blue [&_a]:underline [&_*:last-child]:mb-0">
                {steps[currentStep].consent.text}
              </div>
            </div>
          )}
          {/* Buttons Container */}
          {currentStep < steps.length - 1 && (
            <div className="@container flex flex-wrap gap-[18px] justify-center">
              {currentStep > 0 && (
                <button
                  className="button button--tertiary w-full @[387px]:w-auto"
                  onClick={() => navigateStep(currentStep - 1)}
                >
                  Go Back
                </button>
              )}
              <button
                className="button button--primary w-full @[387px]:w-auto"
                onClick={() => navigateStep(currentStep + 1)}
              >
                {currentStep === 0 ? (
                  `Start`
                ) : currentStep === steps.length - 2 ? (
                  isSubmitted ? (
                    <Loader className="animate-spin" color={"#fff"} />
                  ) : (
                    "Submit"
                  )
                ) : (
                  "Next Step"
                )}
              </button>
            </div>
          )}
        </div>

        {/* MB Card */}
        <div className="bg-white flex flex-col gap-[30px] items-center p-[48px] rounded-[6px] w-[346px] min-tablet2:w-[646px] min-desktop4:self-start min-desktop4:w-[434px]">
          <h5 className="font-variant-numeric--primary sans font-semibold leading-[28px] text-center text-grey-5 text-[22px] tracking-[.22px]">
            Reach out to me if you have any questions:
          </h5>
          <MBHeadshot
            professional={props.bankerData}
            height={220}
            width={220}
          />
          {/* Name and Title */}
          <div>
            <div className="font-variant-numeric--primary flex flex-col font-semibold gap-[21px] leading-[50px] text-balance text-center text-grey-8 text-[44px] -tracking-[.88px]">
              {props.bankerData.name}
            </div>
            <div className="font-variant-numeric--primary leading-[32px] text-balance text-center text-grey-7 text-[22px] tracking-[.22px]">
              {props.bankerData.jobTitle}
            </div>
          </div>
          {/* Licensing */}
          <div className="flex flex-col gap-[5px] [&_*]:caption [&_*]:font-variant-numeric--primary [&_*]:text-grey-6 [&_*]:text-center">
            <div>NMLS #{props.bankerData.nmls}</div>
            <Licensing
              licenses={props.bankerData.licenses}
              stateSpecificLicense={props.bankerData.stateSpecificLicense}
            />
          </div>
          {/* Contact */}
          <div className="font-variant-numeric--primary flex flex-col gap-[10px] leading-[20px] text-balance text-center text-grey-7 text-[16px] underline">
            <a href={`mailto:${props.bankerData.email}`}>
              {props.bankerData.email}
            </a>
            <a href={`tel:${props.bankerData.phone}`}>
              {props.bankerData.phoneText}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
